import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiCaller, QUERY_KEYS } from "@virtual-brain-lib/frontend-api";
import {
	type IntegrationPopupStatus,
	useIntegrationPopup,
} from "@virtual-brain-lib/frontend-utils";
import {
	accountIntegrationNames,
	type MessageToolMailboxConnection,
	type MessageToolMailboxConnectionReason,
} from "@virtual-brain-lib/types";

import { useChatContext } from "../../../chat/core/context/ChatContext";

export type MailboxConnectionState =
	| MessageToolMailboxConnectionReason
	| "waiting"
	| "closed"
	| "connected";

export const useMailboxConnection = (connection: MessageToolMailboxConnection | undefined) => {
	const { type } = useChatContext();
	const queryClient = useQueryClient();
	const canConnect = Boolean(connection) && type !== "chatbot";
	const { data: connectedAccounts = [] } = useQuery(
		ApiCaller.getConnectedAccounts({ enabled: canConnect }),
	);
	const { openIntegrationPopup, popupStatus } = useIntegrationPopup({
		onSuccess() {
			queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connectedAccounts });
		},
	});
	const isMailboxConnected = connectedAccounts.some(
		({ provider, connected }) => provider === connection?.provider && connected.mailbox,
	);

	const connectMailbox = () => {
		if (connection) {
			openIntegrationPopup({
				provider: connection.provider,
				name: accountIntegrationNames[connection.provider],
			});
		}
	};

	return {
		connectionState:
			connection && canConnect
				? _getConnectionState({ connection, isMailboxConnected, popupStatus })
				: undefined,
		connectMailbox,
	};
};

function _getConnectionState({
	connection,
	isMailboxConnected,
	popupStatus,
}: {
	connection: MessageToolMailboxConnection;
	isMailboxConnected: boolean;
	popupStatus: IntegrationPopupStatus;
}): MailboxConnectionState {
	if (isMailboxConnected || popupStatus === "connected") {
		return "connected";
	}

	if (popupStatus === "waiting" || popupStatus === "closed") {
		return popupStatus;
	}

	return connection.reason;
}
